import { Size } from '@lessonup/pins-shared';
import React from 'react';
import { degreesToRadians } from '../../../utils';
import { DEFAULT_ARROW_BODY_THICKNESS, DEFAULT_ARROW_HEAD_ANGLE } from '../shapePinComponent.utils';

export interface ArrowShapeProps {
  size: Size;
  strokeWidth: number;
  /** Angle of the tip arrow head in degrees */
  headAngle?: number;
  /** Thickness of the arrow body, as fraction of the arrows height */
  bodyThickness?: number;
}

export const ArrowShape: React.FC<ArrowShapeProps> = ({
  size,
  strokeWidth,
  headAngle = DEFAULT_ARROW_HEAD_ANGLE,
  bodyThickness = DEFAULT_ARROW_BODY_THICKNESS,
}) => {
  const { width, height } = size;
  const innerWidth = width - strokeWidth * 2;
  const innerHeight = height - strokeWidth * 2;

  // This behaviour mimics Google Slides and Powerpoint.
  // The width of the head is calculated based on the angle of the head.
  // Except when the arrows height exceeds the width, in that case it will clamp the head width based on the width of the arrow.
  // In that case the angle of the head will become wider.
  const radians = degreesToRadians(headAngle) / 2;
  const oppositeSide = innerHeight < innerWidth ? innerHeight / 2 : innerWidth / 2;
  const headWidth = oppositeSide / Math.tan(radians);

  const bodyBottom = height / 2 + (innerHeight / 2) * bodyThickness;
  const bodyTop = height - bodyBottom;
  const bodyLeft = strokeWidth / 2;
  const bodyRight = width - headWidth - strokeWidth / 2;

  // Array of body points, starting from bottom right and going clockwise
  const bodyPoints = [
    `${bodyRight},${bodyBottom}`, // Bottom right
    `${bodyLeft},${bodyBottom}`, // Bottom left
    `${bodyLeft},${bodyTop}`, // Top left
    `${bodyRight},${bodyTop}`, // Top right
  ];

  // Remaining 3 points for the arrow head
  const arrowPoints = [
    `${bodyRight},${strokeWidth / 2}`, // top of the arrow head
    `${width - strokeWidth / 2},${height / 2}`, // center of the arrow head
    `${bodyRight},${height - strokeWidth / 2}`, // bottom of the arrow head
  ];

  const pointsArray = [...bodyPoints, ...arrowPoints];

  return <polygon points={pointsArray.join(' ')} />;
};
